import React from "react";
import { motion } from "framer-motion";
import {
  ClipboardCheck,
  FileText,
  SearchCheck,
  Layers,
  Shield,
  CheckCircle
} from "lucide-react";
import Navbar from "../../components/Navbar";

const auditSteps = [
  {
    title: "Gap Analysis & Planning",
    desc: "Initial assessment to identify gaps against applicable standards and define audit scope.",
    icon: SearchCheck
  },
  {
    title: "Internal Audit Execution",
    desc: "Structured internal audits conducted by experienced professionals using international best practices.",
    icon: ClipboardCheck
  },
  {
    title: "Corrective Action Support",
    desc: "Identification, documentation, and closure of non-conformities with practical solutions.",
    icon: CheckCircle
  }
];

const documentationAreas = [
  {
    title: "Policies & Manuals",
    icon: FileText
  },
  {
    title: "Standard Operating Procedures (SOPs)",
    icon: Layers
  },
  {
    title: "Records, Forms & Logs",
    icon: Shield
  }
];

const AuditsDocumentation = () => {
  return (
    <>
    <Navbar />
    <section className="bg-gray-50 py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            Audits & Documentation
          </h2>
          <div className="w-24 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base md:text-lg">
            MBA Solutions delivers comprehensive audit and documentation
            services to ensure compliance, operational clarity, and
            certification readiness across international standards.
          </p>
        </motion.div>

        {/* ================= AUDIT JOURNEY ================= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-24">
          {auditSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
                whileHover={{
                  y: -12,
                  scale: 1.04,
                  boxShadow: "0px 25px 50px rgba(59,130,246,0.25)"
                }}
                className="relative bg-white rounded-3xl p-8 text-center border border-gray-100 shadow-lg group"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-[var(--bg-primary)] text-white flex items-center justify-center mb-6 shadow-md">
                  <Icon size={28} />
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {step.title}
                </h3>

                <p className="text-sm text-gray-600 leading-relaxed">
                  {step.desc}
                </p>

                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            );
          })}
        </div>

        {/* ================= DOCUMENTATION STACK ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* LEFT – CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Structured Documentation Framework
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6">
              Effective documentation is the backbone of any management
              system. Our experts develop customized documentation aligned
              with ISO, FSC, Halal, Food Safety, and other international
              standards.
            </p>

            <p className="text-gray-600 leading-relaxed">
              We ensure documentation is practical, easy to implement,
              audit-ready, and fully aligned with organizational operations.
            </p>
          </motion.div>

          {/* RIGHT – DOCUMENT TYPES */}
          <div className="space-y-6">
            {documentationAreas.map((doc, index) => {
              const Icon = doc.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.1 }}
                  whileHover={{
                    scale: 1.03,
                    boxShadow: "0px 20px 40px rgba(59,130,246,0.25)"
                  }}
                  className="flex items-center gap-6 bg-white p-6 rounded-2xl shadow-md border border-gray-100"
                >
                  <div className="w-14 h-14 rounded-xl bg-[var(--bg-primary)] text-white flex items-center justify-center shadow-md">
                    <Icon size={24} />
                  </div>
                  <h4 className="text-base font-semibold text-gray-900">
                    {doc.title}
                  </h4>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ================= VALUE OUTCOME ================= */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-24 bg-white rounded-3xl shadow-lg p-10 border border-gray-100"
        >
          <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">
            What You Achieve
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {["Certification Readiness", "Operational Consistency", "Audit Confidence"].map(
              (item, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.05 }}
                  className="text-sm font-medium text-gray-700"
                >
                  ✔ {item}
                </motion.div>
              )
            )}
          </div>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default AuditsDocumentation;
